import {
  trackBillingCheckoutCompleted,
  trackBillingCheckoutStarted,
} from "@/lib/analytics/billing-analytics"
import { isApiSuccessBody } from "@/lib/api/api-envelope"
import type { ApiBillingCheckoutPayload } from "@/lib/api/billing-contract"
import type { BillingInterval, PaidSelfServePlanId } from "@/features/billing/plans"

async function parseCheckoutResponse(
  res: Response,
): Promise<
  | { ok: true; url: string }
  | { ok: true; applied: true; subscriptionId: string }
  | { ok: false; message: string }
> {
  const body: unknown = await res.json()
  if (!res.ok || !isApiSuccessBody<ApiBillingCheckoutPayload>(body)) {
    return { ok: false, message: "Checkout is not available right now." }
  }
  const d = body.data
  if (d.status === "ready" && d.url) return { ok: true, url: d.url }
  if (d.status === "applied") {
    return { ok: true, applied: true, subscriptionId: d.subscriptionId }
  }
  if (d.status === "not_configured") {
    return {
      ok: false,
      message: d.message ?? "Checkout is not configured yet.",
    }
  }
  return { ok: false, message: "Checkout is not configured yet." }
}

export async function requestNewSelfServeSubscriptionCheckout(input: {
  plan: PaidSelfServePlanId
  interval?: BillingInterval
}): Promise<
  { ok: true; url: string } | { ok: false; message: string }
> {
  const res = await fetch("/api/billing/checkout/subscription", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      plan: input.plan,
      interval: input.interval ?? "monthly",
    }),
  })
  const r = await parseCheckoutResponse(res)
  if (r.ok && "url" in r) {
    trackBillingCheckoutStarted({
      kind: "new_subscription",
      plan: input.plan,
      interval: input.interval ?? "monthly",
    })
    return { ok: true, url: r.url }
  }
  if (r.ok && "applied" in r) {
    return { ok: false, message: "Unexpected checkout response." }
  }
  return { ok: false, message: r.ok ? "Unexpected checkout response." : r.message }
}

export async function requestSelfServePlanChangeCheckout(input: {
  plan: PaidSelfServePlanId
  interval?: BillingInterval
}): Promise<
  | { ok: true; url: string }
  | { ok: true; applied: true; subscriptionId: string }
  | { ok: false; message: string }
> {
  const res = await fetch("/api/billing/checkout/plan-change", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      plan: input.plan,
      interval: input.interval ?? "monthly",
    }),
  })
  const r = await parseCheckoutResponse(res)
  if (r.ok && "url" in r) {
    trackBillingCheckoutStarted({
      kind: "plan_change",
      plan: input.plan,
      interval: input.interval ?? "monthly",
    })
    return r
  }
  if (r.ok && "applied" in r && r.applied) {
    trackBillingCheckoutCompleted({ kind: "plan_change_in_app", plan: input.plan })
    return r
  }
  return r
}
